import { ChangeDetectionStrategy, Component, inject, Signal, ViewEncapsulation } from "@angular/core";
import { IconModule } from "@ogs/ngx-m3-components";
import { LogoComponent } from "@shared/components/logo/logo.component";
import { interval, map } from "rxjs";
import { MatListModule } from "@angular/material/list";
import { SidebarListItemComponent } from "@core/components/ui/sidebar/components/list-item/sidebar-list-item.component";
import { RouterLink } from "@angular/router";
import { CORE_ROUTES } from "@core/constants/routes.constant";
import { getUrlSegments } from "@core/utils/navigation.utils";
import { toSignal } from "@angular/core/rxjs-interop";
import { DatePipe, formatDate } from "@angular/common";
import { LanguageSelectionService } from "@shared/components/language-selection/services/language-selection.service";
import { LocaleConfig, TranslationPipeModule } from "@ogs-gmbh/ngx-translate";
import { SidebarService } from "@core/services/sidebar.service";
import { MatTooltip } from "@angular/material/tooltip";

@Component({
  selector: "app-core-sidebar-header",
  templateUrl: "sidebar-header.component.html",
  styleUrl: "sidebar-header.component.scss",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconModule,
    LogoComponent,
    MatListModule,
    SidebarListItemComponent,
    RouterLink,
    DatePipe,
    TranslationPipeModule,
    MatTooltip
  ],
  encapsulation: ViewEncapsulation.Emulated
})
export class SidebarHeaderComponent {
  private readonly _sidebarService: SidebarService = inject(SidebarService);

  // eslint-disable-next-line @tseslint/typedef
  protected readonly CORE_ROUTES = CORE_ROUTES;

  private getNavLevels: Signal<Array<string | null>> = getUrlSegments(1, 2);

  private _languageSelectionService: LanguageSelectionService = inject(LanguageSelectionService);

  protected currentLocale: Signal<LocaleConfig | undefined> = toSignal(this._languageSelectionService.currentLocale$);

  protected currentDateTime: Signal<Date> = toSignal(interval(60 * 1000).pipe(
    map(() => new Date())
  ), { initialValue: new Date() });

  get currentDateString (): string {
    return formatDate(this.currentDateTime(), 'EEE, dd.MM.yyyy', this.currentLocale()?.value ?? 'de');
  }

  get currentTimeString (): string {
    return formatDate(this.currentDateTime(), 'HH:mm', this.currentLocale()?.value ?? 'de');
  }

  isHomeRouted (): boolean {
    return this.getNavLevels()[ 0 ] === CORE_ROUTES.PROJECTS && !this.getNavLevels()[ 1 ];
  }

  get isCollapsed (): boolean {
    return this._sidebarService.collapsed();
  }

  get isCollapsedDelayed (): boolean {
    return this._sidebarService.collapsedDelayed();
  }
}
